import { FC, useEffect, useRef, useState, useMemo } from 'react';
import { Button, ButtonProps, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MicFill, StopCircleFill } from 'react-bootstrap-icons';
import { Loader } from '@/components';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { AudioRecorder, buildWavFileFromResult } from '@/lib/utils/audio-recorder';
import type { AudioRecorderResult } from '@/lib/utils/audio-recorder';
import { useHandleError } from '@/hooks';
import { useDevicePermissions, DEVICE_PERMISSION_STATE } from '@/hooks/use-user-media-permissions';


interface AudioRecorderInstance {
	init(stream: MediaStream): Promise<void>;
	start(): Promise<void>;
	stop(): Promise<AudioRecorderResult>;
}
// Implemented by our local AudioRecorder utility

const useStyles = createUseStyles({
	micButtonActive: {
		// Use currentColor so we inherit Hackett Palette
		animation: '$pulse 1s ease-in-out infinite',
		color: 'inherit',
	},
	'@keyframes pulse': {
		// Fallback pulse uses currentColor; fades to transparent
		'0%': { boxShadow: '0 0 0 0 currentColor' },
		'70%': { boxShadow: '0 0 0 8px rgba(0,0,0,0)' },
		'100%': { boxShadow: '0 0 0 0 rgba(0,0,0,0)' },
	},
	countdownOverlay: {
		position: 'absolute',
		left: '50%',
		top: '50%',
		transform: 'translate(-50%, -50%)',
		background: 'rgba(255,255,255,0.9)',
		padding: '1px 6px',
		borderRadius: 9999,
		fontSize: 14,
		lineHeight: '18px',
		fontWeight: 700,
		zIndex: 2,
		pointerEvents: 'none',
	},
});

export interface VoiceRecordButtonProps {
	onRecorded(file: File): void;
	onRecordingChange?(recording: boolean): void;
	isDisabled?: boolean;
	className?: string;
	buttonVariant?: ButtonProps['variant'];
	buttonSize?: ButtonProps['size'];
	title?: string; // tooltip override
	silenceDebounceMs?: number; // auto-stop after this much silence
	silenceThreshold?: number; // 0..1 normalized amplitude threshold
	isTranscribing?: boolean;
}

export const VoiceRecordButton: FC<VoiceRecordButtonProps> = ({
	onRecorded,
	onRecordingChange,
	isDisabled,
	className,
	buttonVariant = 'link',
	buttonSize = 'sm',
	title,
	silenceDebounceMs = 1500,
	silenceThreshold = 0.02,
	isTranscribing = false,
}) => {
	const classes = useStyles();
	const TARGET_SAMPLE_RATE = 16000;
	const WAV_HEADER_BYTES = 44;
	const OUTPUT_BYTES_PER_SECOND = TARGET_SAMPLE_RATE * 2; // 16kHz mono 16-bit PCM
	const MAX_WAV_BYTES = 10 * 1024 * 1024; // 10 MB size limit
	const COUNTDOWN_WINDOW_SEC = 10; // show countdown when <= 10s remain
	const handleError = useHandleError();
	// Support is determined on demand when the user clicks (permission + MediaRecorder availability)
	const [isRecording, setIsRecording] = useState(false);
	const [analysisReady, setAnalysisReady] = useState(false);
	const [isFinalizing, setIsFinalizing] = useState(false);
	const streamRef = useRef<MediaStream | null>(null);
	const audioRecorderRef = useRef<AudioRecorderInstance | null>(null);
	// Silence detection refs
	const audioContextRef = useRef<AudioContext | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
	const rafIdRef = useRef<number | null>(null);
	const lastLoudTimeRef = useRef<number>(0);
	const dataArrayRef = useRef<Uint8Array | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const colorRgbRef = useRef<string>('');
	const finishingRef = useRef<boolean>(false);
	const recordingStartMsRef = useRef<number | null>(null);
	const [remainingSec, setRemainingSec] = useState<number | null>(null);
	// Centralized device permission management
	const { devicePermissionState, devicePermissionError, handleAccessButtonClick } = useDevicePermissions({
		autoRequestOnMount: false,
	});

	const finalizeToWavFile = async (result?: { blob: Blob; buffer: Float32Array[] }) => {
		try {
			const ctx = audioContextRef.current as AudioContext | null;
			const inputRate = ctx?.sampleRate || 44100;
			if (!result) return;
			const file = await buildWavFileFromResult(result, {
				inputSampleRate: inputRate,
				targetSampleRate: TARGET_SAMPLE_RATE,
				peakThreshold: 0.01,
				fileNamePrefix: 'voice',
			});
			if (!file) {
				// Discard completely silent / inaudible captures.
				return;
			}
			onRecorded(file);
		} catch {
			// If finalize fails, do not submit potentially silent audio
		}
	};

	const updateCurrentColorRgb = () => {
		const el = buttonRef.current;
		if (!el) {
			return;
		}
		try {
			const color = window.getComputedStyle(el).color;
			const m = color.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
			if (m) {
				const rgb = `${m[1]}, ${m[2]}, ${m[3]}`;
				colorRgbRef.current = rgb;
			}
		} catch {}
	};

	useEffect(() => {
		const btnEl = buttonRef.current;
		return () => {
			try {
				// Best-effort stop
				if (audioRecorderRef.current) {
					try {
						audioRecorderRef.current.stop();
					} catch {}
				}
			} catch {}
			streamRef.current?.getTracks()?.forEach((t) => t.stop());
			// cleanup audio graph
			if (rafIdRef.current) {
				cancelAnimationFrame(rafIdRef.current);
			}
			try {
				audioContextRef.current?.close?.();
			} catch {}
			analyserRef.current = null;
			sourceRef.current = null;
			audioContextRef.current = null;
			if (btnEl) {
				btnEl.style.transform = '';
				btnEl.style.boxShadow = '';
			}
		};
	}, []);

	useEffect(() => {
		onRecordingChange?.(isRecording);
	}, [isRecording, onRecordingChange]);

	// Compute base feature support (getUserMedia + AudioContext). We still check locally for a clearer UX.
	const isFeatureUnsupported = useMemo(() => {
		try {
			const mediaOk = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
			const win: any = typeof window !== 'undefined' ? window : undefined;
			const acOk = !!(win && (win.AudioContext || win.webkitAudioContext));
			return !mediaOk || !acOk;
		} catch {
			return true;
		}
	}, []);

	const startRecording = async (inboundStream?: MediaStream) => {
		setAnalysisReady(false);
		try {
			if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
				return;
			}
			const stream = inboundStream ?? (await navigator.mediaDevices.getUserMedia({ audio: true }));
			streamRef.current = stream;
			// Setup AudioContext and local recorder
			const AC = (window.AudioContext || window.webkitAudioContext) as typeof AudioContext | undefined;
			if (!AC) {
				// No AudioContext support
				try {
					stream.getTracks()?.forEach((t) => t.stop());
				} catch {}
				streamRef.current = null;
				return;
			}
			// Use system/default sample rate to avoid Firefox cross-context sample-rate issues
			// We'll downsample to 16 kHz in software when encoding.
			let ctx: AudioContext = new AC();
			audioContextRef.current = ctx;
			try {
				await ctx.resume();
			} catch {}
			const recorder: AudioRecorderInstance = new AudioRecorder(ctx, {});
			await recorder.init(stream);
			audioRecorderRef.current = recorder;
			finishingRef.current = false;
			await recorder.start();
			recordingStartMsRef.current = Date.now();
			setIsRecording(true);

			// Start silence detection loop
			try {
				const src = ctx.createMediaStreamSource(stream);
				const analyser = ctx.createAnalyser();
				analyser.fftSize = 2048;
				src.connect(analyser);
				analyserRef.current = analyser;
				sourceRef.current = src;
				const dataArray = new Uint8Array(analyser.fftSize);
				dataArrayRef.current = dataArray;
				lastLoudTimeRef.current = Date.now();
				setAnalysisReady(true);

				const check = () => {
					if (!analyserRef.current || !dataArrayRef.current) {
						return;
					}
					// Cast to any to satisfy older TS DOM typings
					// Older TypeScript lib.dom.d.ts may expect Uint8Array<ArrayBuffer> rather than ArrayBufferLike
					// Some older TypeScript DOM lib builds mismatch the ArrayBuffer generic; use a thin wrapper.
					const analyser = analyserRef.current;
					const domainArray = dataArrayRef.current;
					if (analyser && domainArray) {
						// Use unknown to avoid introducing 'any' while acknowledging differing lib signatures.
						((analyser.getByteTimeDomainData as unknown) as (array: Uint8Array) => void)(domainArray);
					}
					// Compute PEAK normalized amplitude around midpoint 128
					let maxAbs = 0;
					const analysisArray = dataArrayRef.current;
					for (let i = 0; analysisArray && i < analysisArray.length; i++) {
						const v = (analysisArray[i] - 128) / 128; // -1..1
						const a = Math.abs(v);
						if (a > maxAbs) maxAbs = a;
					}
					const amp = maxAbs; // 0..1 (peak)

					// Size-based auto-stop and countdown
					if (recordingStartMsRef.current) {
						const elapsedSec = Math.max(0, (Date.now() - recordingStartMsRef.current) / 1000);
						const predictedBytes = WAV_HEADER_BYTES + OUTPUT_BYTES_PER_SECOND * elapsedSec;
						const remainingBytes = MAX_WAV_BYTES - predictedBytes;
						const remainSec = Math.ceil(Math.max(0, remainingBytes / OUTPUT_BYTES_PER_SECOND));
						setRemainingSec(Number.isFinite(remainSec) ? remainSec : null);
						if (remainingBytes <= 0 && audioRecorderRef.current && !finishingRef.current) {
							// Reached size limit, stop recording
							if (rafIdRef.current) {
								cancelAnimationFrame(rafIdRef.current);
							}
							rafIdRef.current = null;
							finishingRef.current = true;
							setIsFinalizing(true);
							(async () => {
								try {
									if (!audioRecorderRef.current) return;
									const result = await audioRecorderRef.current.stop();
									await finalizeToWavFile(result);
								} catch {}
								// cleanup
								streamRef.current?.getTracks()?.forEach((t) => t.stop());
								streamRef.current = null;
								audioRecorderRef.current = null;
								try {
									audioContextRef.current?.close?.();
								} catch {}
								analyserRef.current = null;
								sourceRef.current = null;
								audioContextRef.current = null;
								if (buttonRef.current) {
									buttonRef.current.style.transform = '';
									buttonRef.current.style.boxShadow = '';
								}
								setAnalysisReady(false);
								setIsRecording(false);
								setIsFinalizing(false);
								setRemainingSec(null);
								recordingStartMsRef.current = null;
							})();
							return;
						}
					}
					// Animate button based on amplitude
					if (buttonRef.current) {
						updateCurrentColorRgb();
						const norm = Math.max(0, Math.min(1, (amp - silenceThreshold) / 0.15));
						const scale = 1 + norm * 0.04; // toned down to +4%
						const blur = Math.round(2 + norm * 6); // reduced glow spread
						const alpha = 0.15 + norm * 0.25; // reduced intensity
						buttonRef.current.style.transform = `scale(${scale})`;
						const rgb = colorRgbRef.current || '0, 0, 0';
						buttonRef.current.style.boxShadow = `0 0 ${blur}px rgba(${rgb}, ${alpha})`;
					}
					if (amp > silenceThreshold) {
						lastLoudTimeRef.current = Date.now();
					}
					const silentFor = Date.now() - lastLoudTimeRef.current;
					if (silentFor >= silenceDebounceMs && audioRecorderRef.current && !finishingRef.current) {
						// stop due to silence
						if (rafIdRef.current) {
							cancelAnimationFrame(rafIdRef.current);
						}
						rafIdRef.current = null;
						finishingRef.current = true;
						setIsFinalizing(true);
						(async () => {
							try {
								if (!audioRecorderRef.current) {
									return; // safety guard
								}
								const result = await audioRecorderRef.current.stop();
								await finalizeToWavFile(result);
							} catch {}
							// cleanup
							streamRef.current?.getTracks()?.forEach((t) => t.stop());
							streamRef.current = null;
							audioRecorderRef.current = null;
							try {
								audioContextRef.current?.close?.();
							} catch {}
							analyserRef.current = null;
							sourceRef.current = null;
							audioContextRef.current = null;
							if (buttonRef.current) {
								buttonRef.current.style.transform = '';
								buttonRef.current.style.boxShadow = '';
							}
							setAnalysisReady(false);
							setIsRecording(false);
							setIsFinalizing(false);
						})();
						return;
					}
					rafIdRef.current = requestAnimationFrame(check);
				};
				rafIdRef.current = requestAnimationFrame(check);
			} catch (e) {
				// If AudioContext path fails, continue recording without auto-stop
			}
		} catch (err) {
			// Permission or device acquisition failed; rely on permission hook's state for messaging.
			setIsRecording(false);
		}
	};

	const stopRecording = () => {
		if (!audioRecorderRef.current || finishingRef.current) {
			return;
		}
		finishingRef.current = true;
		setIsFinalizing(true);
		(async () => {
			try {
				if (!audioRecorderRef.current) {
					return; // guard
				}
				const result = await audioRecorderRef.current.stop();
				await finalizeToWavFile(result);
			} catch {}
			// cleanup
			streamRef.current?.getTracks()?.forEach((t) => t.stop());
			streamRef.current = null;
			audioRecorderRef.current = null;
			if (rafIdRef.current) {
				cancelAnimationFrame(rafIdRef.current);
			}
			try {
				audioContextRef.current?.close?.();
			} catch {}
			analyserRef.current = null;
			sourceRef.current = null;
			audioContextRef.current = null;
			if (buttonRef.current) {
				buttonRef.current.style.transform = '';
				buttonRef.current.style.boxShadow = '';
			}
			setAnalysisReady(false);
			setIsRecording(false);
			setIsFinalizing(false);
			setRemainingSec(null);
			recordingStartMsRef.current = null;
		})();
	};

	const buttonDisabled =
		isDisabled || isFinalizing || isTranscribing || isFeatureUnsupported;

	const toggle = () => {
		if (buttonDisabled) {
			return;
		}
		if (isRecording) {
			stopRecording();
		} else {
			if (devicePermissionState !== DEVICE_PERMISSION_STATE.GRANTED) {
				// Prompt for permission/devices first, then start on success
				handleAccessButtonClick(
					(stream) => startRecording(stream),
					() => {
						if (devicePermissionError) {
							// Surface error through app handler for consistency
							handleError(devicePermissionError);
						}
					}
				);
			} else {
				startRecording();
			}
		}
	};

	const tooltipText = useMemo(() => {
		if (isFeatureUnsupported) {
			return 'Voice dictation not supported';
		}
		if (devicePermissionState === DEVICE_PERMISSION_STATE.DENIED) {
			return 'Mic access denied - click to retry';
		}
		if (isRecording) {
			return 'Stop Dictation';
		}
		if (isTranscribing) {
			return 'Transcribing audio…';
		}
		return title || 'Start Dictation';
	}, [isFeatureUnsupported, devicePermissionState, isRecording, isTranscribing, title]);

	return (
		<OverlayTrigger
			placement="top"
			delay={{ show: 250, hide: 250 }}
			overlay={
				<Tooltip>
					<p className="mb-0 small">{tooltipText}</p>
				</Tooltip>
			}
		>
			<span>
				<Button
					ref={buttonRef}
					type="button"
					variant={buttonVariant}
					size={buttonSize}
					onClick={toggle}
					aria-pressed={isRecording}
					aria-label={
						isRecording ? 'Stop Dictation' : isTranscribing ? 'Transcribing audio' : 'Start Dictation'
					}
					disabled={buttonDisabled}
					className={classNames(
						className,
						isRecording && 'bg-danger text-danger',
						!isRecording && !buttonDisabled && !isTranscribing && 'text-primary',
						isRecording && !analysisReady && classes.micButtonActive
					)}
					style={{ backgroundColor: isRecording ? undefined : 'transparent', border: 0, position: 'relative' }}
				>
					{isTranscribing ? (
						<Loader size={20} className="d-flex" />
					) : isRecording ? (
						<StopCircleFill className="d-flex text-white" width={20} height={20} />
					) : (
						<MicFill className="d-flex" width={20} height={20} />
					)}
					{isRecording && remainingSec !== null && remainingSec <= COUNTDOWN_WINDOW_SEC && (
						<span className={classes.countdownOverlay}>{remainingSec}s</span>
					)}
				</Button>
			</span>
		</OverlayTrigger>
	);
};

export default VoiceRecordButton;
